import {useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {Hotel} from '@findhotel/sapi'

import {trackProviderRedirect} from '@vio/core/src/analytics/modules/actions'
import {removeOnAuthSuccessLandingUrl} from '@vio/core/src/auth/business/persistData'

import {useCreateAnalyticsLeadContext} from '../components/DealFreezeButton/useCreateAnalyticsLeadContext'
import {openFreezeOverlay, setAnalyticsContext} from '../modules/slice'
import {clearFreezeIntent, readFreezeIntent} from '../utils/freezeIntent'
import {readDealFreezeStash} from '../utils/stash'

/**
 * Lets the sign-in surface return to the freeze overlay instead of signing in.
 * Rebuilt from the stash because the overlay close already reset the redux
 * data; the stash itself is kept so a re-commit can still resume.
 * @param isEnabled - Read storage only while the DealFreeze sign-in is open
 */
export const useBackToFreezeOverlay = (isEnabled: boolean) => {
  const dispatch = useDispatch()

  const stash = useMemo(
    () => (isEnabled ? readDealFreezeStash() : null),
    [isEnabled]
  )
  const intent = useMemo(
    () => (isEnabled ? readFreezeIntent() : null),
    [isEnabled]
  )

  const createAnalyticsLeadContext = useCreateAnalyticsLeadContext({
    ...(stash?.leadContextRequiredData ?? {}),
    trackProviderRedirect,
    offerId: stash?.roomOffer?.id ?? ''
  })

  // The hotel match keeps a stale intent from another hotel's commit from
  // offering a way back, mirroring the resume's guard.
  const canGoBack = Boolean(
    intent &&
      stash?.roomOffer &&
      stash.hotel &&
      intent.hotelId === (stash.hotel as Hotel).objectID
  )

  const goBack = (closeSignIn: () => void) => {
    clearFreezeIntent()
    removeOnAuthSuccessLandingUrl()
    closeSignIn()
    if (!stash?.roomOffer || !stash.hotel) return
    if (stash.analyticsContext) {
      dispatch(setAnalyticsContext(stash.analyticsContext))
    }
    dispatch(
      openFreezeOverlay({
        offer: {
          id: stash.roomOffer.id,
          providerCode: stash.roomOffer.providerCode,
          links: stash.roomOffer.links
        },
        hotel: stash.hotel as Hotel,
        searchDetail: stash.searchDetail,
        room: stash.room && [stash.room],
        component: stash.component,
        fromOnboarding: intent?.fromOnboarding
      })
    )
    createAnalyticsLeadContext()
  }

  return {canGoBack, goBack}
}
